<script setup lang="ts">
import EdgeOptionField from "@/flowcharts/Components/EdgeOptionField.vue";
import {EdgeOptions} from "@/core/interfaces/flowchart/flowchart";
import {computed} from "vue";

const props = defineProps<{
    selectedEdges: { id: number, nodeLabel: string; label: string; slug: string, options?: EdgeOptions }[],
    fieldValue: any
}>();
defineEmits<{
    (e: 'handle-option-change', id: number, fieldSlug: string, value: string | string[]): void;
}>();

/**
 * The edges worth recalling: an unlabeled edge without options only leads to the
 * next node, which already names the answer.
 */
const displayedEdges = computed(() => props.selectedEdges.filter(edge => edge.label || edge.options));

/**
 * Sorts the edge options by priority.
 */
const sortedOptions = (options: EdgeOptions): EdgeOptions => {
    const entries = Object.entries(options);
    entries.sort(([_a, fieldA], [_b, fieldB]) => {
        const orderA = fieldA.priority ?? Infinity;
        const orderB = fieldB.priority ?? Infinity;
        return orderA - orderB;
    });

    return Object.fromEntries(entries);
};
</script>

<template>
  <div v-if="displayedEdges.length" class="flex flex-col gap-y-1 text-sm">
    <div v-for="(edge, index) in displayedEdges" :key="index" class="flex flex-col gap-3">
      <div class="flex gap-1">
        <div class="line-clamp-1 text-ellipsis">
          {{ edge.nodeLabel }}
        </div>
        :
        <div class="flex-1 font-bold">
          {{ edge.label }}
        </div>
      </div>
      <div v-if="edge.options" class="flex flex-col gap-4 border-t pt-3">
        <div
          v-for="(field, fieldSlug) in sortedOptions(edge.options)"
          :key="`option-${edge.id}-${fieldSlug}`"
        >
          <EdgeOptionField
            :field="field"
            :field-slug="String(fieldSlug)"
            :model-value="fieldValue(edge.id, String(fieldSlug), field)"
            @update:model-value="(value) => $emit('handle-option-change',edge.id, String(fieldSlug), value)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>
